/** EML-050: Email sequences service */
import { supabase } from '../client';

export interface EmailSequenceStep {
  id: string;
  sequence_id: string;
  step_order: number;
  delay_days: number;
  template_key: string;
  subject: string;
  body_html: string;
  created_at: string;
}

export interface EmailSequence {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  trigger_event: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  steps?: EmailSequenceStep[];
}

export const emailSequenceService = {
  async list(orgId: string): Promise<EmailSequence[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_email_sequences')
      .select('*')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as EmailSequence[];
  },

  async get(id: string): Promise<EmailSequence & { steps: EmailSequenceStep[] }> {
    const [{ data: seq, error: seqErr }, { data: steps, error: stepsErr }] = await Promise.all([
      supabase.from('ggj_b2b_email_sequences').select('*').eq('id', id).single(),
      supabase
        .from('ggj_b2b_email_sequence_steps')
        .select('*')
        .eq('sequence_id', id)
        .order('step_order'),
    ]);
    if (seqErr) throw seqErr;
    if (stepsErr) throw stepsErr;
    return { ...(seq as EmailSequence), steps: (steps ?? []) as EmailSequenceStep[] };
  },

  async create(orgId: string, payload: { name: string; description?: string; trigger_event?: string }): Promise<EmailSequence> {
    const { data, error } = await supabase
      .from('ggj_b2b_email_sequences')
      .insert({ org_id: orgId, ...payload, is_active: false })
      .select()
      .single();
    if (error) throw error;
    return data as EmailSequence;
  },

  async update(id: string, payload: Partial<Pick<EmailSequence, 'name' | 'description' | 'trigger_event' | 'is_active'>>): Promise<void> {
    const { error } = await supabase.from('ggj_b2b_email_sequences').update(payload).eq('id', id);
    if (error) throw error;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase.from('ggj_b2b_email_sequences').delete().eq('id', id);
    if (error) throw error;
  },

  async addStep(
    sequenceId: string,
    step: { step_order: number; delay_days: number; template_key: string; subject: string; body_html: string }
  ): Promise<EmailSequenceStep> {
    const { data, error } = await supabase
      .from('ggj_b2b_email_sequence_steps')
      .insert({ sequence_id: sequenceId, ...step })
      .select()
      .single();
    if (error) throw error;
    return data as EmailSequenceStep;
  },

  async updateStep(
    stepId: string,
    patch: Partial<Pick<EmailSequenceStep, 'step_order' | 'delay_days' | 'template_key' | 'subject' | 'body_html'>>,
  ): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_email_sequence_steps')
      .update(patch)
      .eq('id', stepId);
    if (error) throw error;
  },

  async deleteStep(stepId: string): Promise<void> {
    const { error } = await supabase.from('ggj_b2b_email_sequence_steps').delete().eq('id', stepId);
    if (error) throw error;
  },
};
